import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MODAL_CONTROLLER, MODAL_DATA, ModalController } from 'shared';
import { DeviceDataPoint } from '../../interfaces/data-points';
import { UserTrackingHelperService } from '../../services/user-tracking-helper.service';
import { SystemControlMode } from '../../interfaces/system-control-mode';
import { DataPointsService } from '../../services/data-points.service';
import { ModalComponent } from '../../../../../shared/src/lib/components/modal/modal.component';
import { ControlStateToggleComponent } from '../control-state-toggle/control-state-toggle.component';
import { ModalContentComponent } from '../../../../../shared/src/lib/components/modal-content/modal-content.component';
import { ModalSectionComponent } from '../../../../../shared/src/lib/components/modal-section/modal-section.component';
import { ModalSectionColumnComponent } from '../../../../../shared/src/lib/components/modal-section-column/modal-section-column.component';
import { InputLabelComponent } from '../../../../../shared/src/lib/components/input-label/input-label.component';
import { TooltipModule } from 'primeng/tooltip';
import { InputFieldComponent } from '../../../../../shared/src/lib/components/input-field/input-field.component';
import { ModalButtonsComponent } from '../../../../../shared/src/lib/components/modal-buttons/modal-buttons.component';
import { ModalButtonComponent } from '../../../../../shared/src/lib/components/modal-button/modal-button.component';
import { Angulartics2OnModule } from 'angulartics2';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

export interface ValveModalInput {
  applicationId: string;
  installationId: string;
  openingSetpoint: DeviceDataPoint | null;
  max?: number;
  min?: number;
}

export interface ValveModalResponse {
  openingSetpoint: number;
  selectedSystemControlMode: SystemControlMode;
}

@Component({
    selector: 'app-valve-modal',
    templateUrl: './valve-modal.component.html',
    styleUrls: ['./valve-modal.component.scss'],
    imports: [
        ModalComponent,
        ControlStateToggleComponent,
        ModalContentComponent,
        FormsModule,
        ReactiveFormsModule,
        ModalSectionComponent,
        ModalSectionColumnComponent,
        InputLabelComponent,
        TooltipModule,
        InputFieldComponent,
        ModalButtonsComponent,
        ModalButtonComponent,
        Angulartics2OnModule,
        TranslateModule,
        AsyncPipe,
    ]
})
export class ValveModalComponent implements OnInit, OnDestroy {
  public min: number;
  public max: number;
  public form: FormGroup;
  public setpoint?: number;
  public isValid$: Observable<boolean>;
  private subscription = new Subscription();
  public SystemControlMode = SystemControlMode;
  currentSystemControlMode: SystemControlMode;
  selectedSystemControlMode: SystemControlMode;
  subscribedSystemControlMode: Subscription;

  constructor(
    @Inject(MODAL_DATA) public data: ValveModalInput,
    @Inject(MODAL_CONTROLLER) private controller: ModalController<ValveModalResponse>,
    private dataPointsService: DataPointsService,
    private userTrackingHelperService: UserTrackingHelperService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.min = this.data.min ? this.data.min : 0;
    this.max = this.data.max ? this.data.max : 100;
    this.setpoint = parseInt(Number(this.data.openingSetpoint?.value).toFixed(0));
    if (isNaN(this.setpoint)) {
      this.setpoint = undefined;    // Necessary to trigger the Required validator (as it considers NaN to be a valid value)
    }

    this.subscribedSystemControlMode = this.dataPointsService.getSystemControlMode(this.data.applicationId).subscribe((event) => {
      this.currentSystemControlMode = event;
    });
    this.selectedSystemControlMode = this.currentSystemControlMode;

    this.form = this.formBuilder.group({
      openingSetpoint: [{ value: this.setpoint, disabled: this.isAutoModeEnabled() || this.selectedSystemControlMode === SystemControlMode.Unknown }, [
        Validators.required,
        Validators.min(this.min),
        Validators.max(this.max),
      ]],
    });

    this.subscription.add(this.userTrackingHelperService.startFormValueChangeTracking('valveSetpointDialog', this.form));

    this.isValid$ = this.form.valueChanges.pipe(
      map((value) => {
        if (this.isAutoModeEnabled()) return true;
        return this.form.valid;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscribedSystemControlMode.unsubscribe();
  }

  close() {
    this.userTrackingHelperService.trackUserAction('valveSetpointDialog', 'closeClicked');
    this.controller.dismiss();
  }

  save() {
    this.controller.complete({
      openingSetpoint: this.form.controls.openingSetpoint.value,
      selectedSystemControlMode: this.selectedSystemControlMode
    });
  }

  dismiss() {
    this.userTrackingHelperService.trackUserAction('valveSetpointDialog', 'exitClicked');
  }

  isAutoModeEnabled(): boolean {
    return this.selectedSystemControlMode === SystemControlMode.Auto;
  }

  selectApplicationControlMode(selectedApplicationControlMode: SystemControlMode) {
    this.selectedSystemControlMode = selectedApplicationControlMode;
    if (this.isAutoModeEnabled()) {
      this.form.controls.openingSetpoint.disable();
    } else {
      this.form.controls.openingSetpoint.enable();
    }
  }
}
